import { useCallback, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { MealNeedsApi, MealNeed, CreateMealNeedRequest, UpdateMealNeedRequest, CreateMealContributorRequest, UpdateMealContributorRequest } from '@/shared/api/mealNeeds'
import { useAuth } from '@/modules/auth'
import type { TripWithDetailsResponse } from '@/shared/api/types'

interface UseMealNeedsReturn {
  meals: MealNeed[]
  isLoading: boolean
  isCreating: boolean
  isUpdating: boolean
  isDeleting: boolean
  isContributing: boolean
  isOptingOut: boolean
  error: string | null
  createMeal: (data: CreateMealNeedRequest) => Promise<MealNeed>
  updateMeal: (mealId: string, data: UpdateMealNeedRequest) => Promise<MealNeed>
  deleteMeal: (mealId: string) => Promise<void>
  addMealContributor: (mealId: string, data: CreateMealContributorRequest) => Promise<void>
  updateMealContributor: (mealId: string, data: UpdateMealContributorRequest) => Promise<void>
  removeMealContributor: (mealId: string) => Promise<void>
  optOutOfMeal: (mealId: string) => Promise<void>
  optInToMeal: (mealId: string) => Promise<void>
  refreshMeals: () => Promise<void>
}

export function useMealNeeds(tripId: string, options?: { skip?: boolean }): UseMealNeedsReturn {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const { skip = false } = options || {}

  // Create API instance with auth token
  const getAuthToken = useCallback(async (): Promise<string | null> => {
    return localStorage.getItem('access_token')
  }, [])

  const mealNeedsApi = useMemo(() => new MealNeedsApi(getAuthToken), [getAuthToken])

  // The trip cache only seeds the first render. A refetch always asks the API: after an
  // invalidation the trip cache can still hold the old meals, and would hide what just changed.
  const { data: meals = [], isLoading, error } = useQuery({
    queryKey: ['meals', tripId],
    queryFn: () => mealNeedsApi.getTripMeals(tripId),
    enabled: !skip && !!tripId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    initialData: () => {
      const tripData = queryClient.getQueryData<TripWithDetailsResponse>(['trip', tripId])
      const cachedMeals = tripData?.meals
      if (cachedMeals && Array.isArray(cachedMeals) && cachedMeals.length > 0) {
        return cachedMeals as unknown as MealNeed[]
      }
      return undefined
    }
  })

  // Create meal mutation
  const createMutation = useMutation({
    mutationFn: (data: CreateMealNeedRequest) => mealNeedsApi.createMeal(tripId, data),
    onSuccess: (newMeal) => {
      queryClient.setQueryData<MealNeed[]>(['meals', tripId], (old = []) => [...old, newMeal])
      queryClient.setQueryData<TripWithDetailsResponse>(['trip', tripId], (old) => 
        old ? { ...old, meals: [...(old.meals || []), newMeal] } as TripWithDetailsResponse : old
      )
    }
  })

  // Update meal mutation
  const updateMutation = useMutation({
    mutationFn: ({ mealId, data }: { mealId: string; data: UpdateMealNeedRequest }) => 
      mealNeedsApi.updateMeal(mealId, data),
    onSuccess: (updatedMeal, { mealId }) => {
      queryClient.setQueryData<MealNeed[]>(['meals', tripId], (old = []) => 
        old.map(meal => meal.id === mealId ? updatedMeal : meal)
      )
      queryClient.setQueryData<TripWithDetailsResponse>(['trip', tripId], (old) => 
        old ? { ...old, meals: (old.meals || []).map(m => m.id === mealId ? updatedMeal : m) } as TripWithDetailsResponse : old
      )
    }
  })

  // Delete meal mutation
  const deleteMutation = useMutation({
    mutationFn: (mealId: string) => mealNeedsApi.deleteMeal(mealId),
    onSuccess: (_, mealId) => {
      queryClient.setQueryData<MealNeed[]>(['meals', tripId], (old = []) => 
        old.filter(meal => meal.id !== mealId)
      )
      queryClient.setQueryData<TripWithDetailsResponse>(['trip', tripId], (old) => 
        old ? { ...old, meals: (old.meals || []).filter(m => m.id !== mealId) } as TripWithDetailsResponse : old
      )
    }
  })

  // Add meal contributor mutation
  const addContributorMutation = useMutation({
    mutationFn: ({ mealId, data }: { mealId: string; data: CreateMealContributorRequest }) => 
      mealNeedsApi.addMealContributor(mealId, data),
    onSuccess: async (_, { mealId }) => {
      const updatedMeal = await mealNeedsApi.getMealById(mealId)
      queryClient.setQueryData<MealNeed[]>(['meals', tripId], (old = []) => 
        old.map(meal => meal.id === mealId ? updatedMeal : meal)
      )
      queryClient.setQueryData<TripWithDetailsResponse>(['trip', tripId], (old) => 
        old ? { ...old, meals: (old.meals || []).map(m => m.id === mealId ? updatedMeal : m) } as TripWithDetailsResponse : old
      )
    }
  })

  // Update meal contributor mutation
  const updateContributorMutation = useMutation({
    mutationFn: ({ mealId, data }: { mealId: string; data: UpdateMealContributorRequest }) => 
      mealNeedsApi.updateMealContributor(mealId, data),
    onSuccess: async (_, { mealId }) => {
      const updatedMeal = await mealNeedsApi.getMealById(mealId)
      queryClient.setQueryData<MealNeed[]>(['meals', tripId], (old = []) => 
        old.map(meal => meal.id === mealId ? updatedMeal : meal)
      )
      queryClient.setQueryData<TripWithDetailsResponse>(['trip', tripId], (old) => 
        old ? { ...old, meals: (old.meals || []).map(m => m.id === mealId ? updatedMeal : m) } as TripWithDetailsResponse : old
      )
    }
  })

  // Remove meal contributor mutation
  const removeContributorMutation = useMutation({
    mutationFn: (mealId: string) => mealNeedsApi.removeMealContributor(mealId),
    onSuccess: async (_, mealId) => {
      const updatedMeal = await mealNeedsApi.getMealById(mealId)
      queryClient.setQueryData<MealNeed[]>(['meals', tripId], (old = []) => 
        old.map(meal => meal.id === mealId ? updatedMeal : meal)
      )
      queryClient.setQueryData<TripWithDetailsResponse>(['trip', tripId], (old) => 
        old ? { ...old, meals: (old.meals || []).map(m => m.id === mealId ? updatedMeal : m) } as TripWithDetailsResponse : old
      )
    }
  })

  const updateMealInCache = useCallback((mealId: string, updatedMeal: MealNeed) => {
    queryClient.setQueryData<MealNeed[]>(['meals', tripId], (old = []) => 
      old.map(meal => meal.id === mealId ? updatedMeal : meal)
    )
    queryClient.setQueryData<TripWithDetailsResponse>(['trip', tripId], (old) => 
      old ? { ...old, meals: (old.meals || []).map(m => m.id === mealId ? updatedMeal : m) } as TripWithDetailsResponse : old
    )
  }, [queryClient, tripId])

  // Opt out of meal mutation
  const optOutMutation = useMutation({
    mutationFn: (mealId: string) => mealNeedsApi.optOutOfMeal(mealId),
    onSuccess: async (_, mealId) => {
      const updatedMeal = await mealNeedsApi.getMealById(mealId)
      updateMealInCache(mealId, updatedMeal)
    }
  })

  // Opt in to meal mutation
  const optInMutation = useMutation({
    mutationFn: (mealId: string) => mealNeedsApi.optInToMeal(mealId),
    onSuccess: async (_, mealId) => {
      const updatedMeal = await mealNeedsApi.getMealById(mealId)
      updateMealInCache(mealId, updatedMeal)
    }
  })

  // Wrapper functions for mutations
  const createMeal = useCallback(async (data: CreateMealNeedRequest): Promise<MealNeed> => {
    if (!tripId) throw new Error('Trip ID is required')
    return await createMutation.mutateAsync(data)
  }, [tripId, createMutation])

  const updateMeal = useCallback(async (mealId: string, data: UpdateMealNeedRequest): Promise<MealNeed> => {
    return await updateMutation.mutateAsync({ mealId, data })
  }, [updateMutation])

  const deleteMeal = useCallback(async (mealId: string): Promise<void> => {
    await deleteMutation.mutateAsync(mealId)
  }, [deleteMutation])

  const addMealContributor = useCallback(async (mealId: string, data: CreateMealContributorRequest): Promise<void> => {
    if (!user) throw new Error('User not authenticated')
    await addContributorMutation.mutateAsync({ mealId, data })
  }, [user, addContributorMutation])

  const updateMealContributor = useCallback(async (mealId: string, data: UpdateMealContributorRequest): Promise<void> => {
    if (!user) throw new Error('User not authenticated')
    await updateContributorMutation.mutateAsync({ mealId, data })
  }, [user, updateContributorMutation])

  const removeMealContributor = useCallback(async (mealId: string): Promise<void> => {
    if (!user) throw new Error('User not authenticated')
    await removeContributorMutation.mutateAsync(mealId)
  }, [user, removeContributorMutation])

  const optOutOfMeal = useCallback(async (mealId: string): Promise<void> => {
    if (!user) throw new Error('User not authenticated')
    await optOutMutation.mutateAsync(mealId)
  }, [user, optOutMutation])

  const optInToMeal = useCallback(async (mealId: string): Promise<void> => {
    if (!user) throw new Error('User not authenticated')
    await optInMutation.mutateAsync(mealId)
  }, [user, optInMutation])

  const refreshMeals = useCallback(async (): Promise<void> => {
    if (skip) return
    // Only refetch if data is stale (prevents duplicate refetches after our own mutations)
    await queryClient.refetchQueries({ 
      queryKey: ['meals', tripId],
      type: 'active'
    })
    await queryClient.refetchQueries({ 
      queryKey: ['trip', tripId],
      type: 'active'
    })
  }, [queryClient, tripId, skip])

  return {
    meals,
    isLoading,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
    isContributing: addContributorMutation.isPending || updateContributorMutation.isPending || removeContributorMutation.isPending,
    isOptingOut: optOutMutation.isPending || optInMutation.isPending,
    error: (error as Error)?.message || null,
    createMeal,
    updateMeal,
    deleteMeal,
    addMealContributor,
    updateMealContributor,
    removeMealContributor,
    optOutOfMeal,
    optInToMeal,
    refreshMeals
  }
}
